import { useMutation, useQuery } from '@tanstack/react-query';
import type {
    DataTag,
    DefinedInitialDataOptions,
    DefinedUseQueryResult,
    MutationFunction,
    QueryClient,
    QueryFunction,
    QueryKey,
    UndefinedInitialDataOptions,
    UseMutationOptions,
    UseMutationResult,
    UseQueryOptions,
    UseQueryResult
} from '@tanstack/react-query';

import { useCallback } from 'react';

import type {
    CreateArticleModel,
    CreatePrefabModel,
    GetAllArticlesParams,
    GetAllArticlesResponse,
    GetArticleByIdResponse,
    GetArticleByNumberResponse,
    GetArticleMetricsParams,
    GetArticleMetricsResponse,
    GetArticlesByElementTypeResponse,
    GetArticlesBySettingTypeParams,
    GetArticlesBySettingTypeResponse,
    GetArticlesBySystemIdResponse,
    GetArticlesBySystemParams,
    GetMainRepresentationByArticleIdParams,
    GetMainRepresentationByArticleIdResponse,
    GetPrefabSummariesByArticleIdResponse,
    ImportPrefabByArticleIdParams,
    ProblemDetails,
    UpdatePrefabModel
} from '../../models';

import { useCustomAppClient } from '../../custom-client';
import type { ErrorType } from '../../custom-client';

/**
 * Get article by id
 */
export const useGetArticleByIdHook = () => {
    const getArticleById = useCustomAppClient<GetArticleByIdResponse>();

    return useCallback(
        (id: string, signal?: AbortSignal) => {
            return getArticleById({ url: `/articles/${id}`, method: 'GET', signal });
        },
        [getArticleById]
    );
};

export const getGetArticleByIdQueryKey = (id?: string) => {
    return [`/articles/${id}`] as const;
};

export const useGetArticleByIdQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    id: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>, TError, TData>>;
    }
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetArticleByIdQueryKey(id);

    const getArticleById = useGetArticleByIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>> = ({ signal }) =>
        getArticleById(id, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetArticleByIdQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>>;
export type GetArticleByIdQueryError = ErrorType<ProblemDetails>;

export function useGetArticleById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    id: string,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetArticleById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    id: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetArticleById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    id: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetArticleById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    id: string,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetArticleByIdQueryOptions(id, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    query.queryKey = queryOptions.queryKey;

    return query;
}

/**
 * Delete article by id
 */
export const useDeleteArticleByIdHook = () => {
    const deleteArticleById = useCustomAppClient<void>();

    return useCallback(
        (id: string) => {
            return deleteArticleById({ url: `/articles/${id}`, method: 'DELETE' });
        },
        [deleteArticleById]
    );
};

export const useDeleteArticleByIdMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteArticleByIdHook>>>,
        TError,
        { id: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteArticleByIdHook>>>,
    TError,
    { id: string },
    TContext
> => {
    const mutationKey = ['deleteArticleById'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const deleteArticleById = useDeleteArticleByIdHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeleteArticleByIdHook>>>,
        { id: string }
    > = (props) => {
        const { id } = props ?? {};

        return deleteArticleById(id);
    };

    return { mutationFn, ...mutationOptions };
};

export type DeleteArticleByIdMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useDeleteArticleByIdHook>>>
>;

export type DeleteArticleByIdMutationError = ErrorType<ProblemDetails>;

export const useDeleteArticleById = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteArticleByIdHook>>>,
            TError,
            { id: string },
            TContext
        >;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeleteArticleByIdHook>>>,
    TError,
    { id: string },
    TContext
> => {
    const mutationOptions = useDeleteArticleByIdMutationOptions(options);

    return useMutation(mutationOptions, queryClient);
};
/**
 * Get article by articleNumber
 */
export const useGetArticleByNumberHook = () => {
    const getArticleByNumber = useCustomAppClient<GetArticleByNumberResponse>();

    return useCallback(
        (articleNumber: string, signal?: AbortSignal) => {
            return getArticleByNumber({ url: `/articles/${articleNumber}`, method: 'GET', signal });
        },
        [getArticleByNumber]
    );
};

export const getGetArticleByNumberQueryKey = (articleNumber?: string) => {
    return [`/articles/${articleNumber}`] as const;
};

export const useGetArticleByNumberQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    articleNumber: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>, TError, TData>
        >;
    }
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetArticleByNumberQueryKey(articleNumber);

    const getArticleByNumber = useGetArticleByNumberHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>> = ({ signal }) =>
        getArticleByNumber(articleNumber, signal);

    return { queryKey, queryFn, enabled: !!articleNumber, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetArticleByNumberQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>
>;
export type GetArticleByNumberQueryError = ErrorType<ProblemDetails>;

export function useGetArticleByNumber<
    TData = Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    articleNumber: string,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetArticleByNumber<
    TData = Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    articleNumber: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetArticleByNumber<
    TData = Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    articleNumber: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetArticleByNumber<
    TData = Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    articleNumber: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetArticleByNumberQueryOptions(articleNumber, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    query.queryKey = queryOptions.queryKey;

    return query;
}

/**
 * Get article metrics by article numbers
 */
export const useGetArticleMetricsHook = () => {
    const getArticleMetrics = useCustomAppClient<GetArticleMetricsResponse>();

    return useCallback(
        (getArticleMetricsBody: string[], params: GetArticleMetricsParams, signal?: AbortSignal) => {
            return getArticleMetrics({
                url: `/articles/metrics`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: getArticleMetricsBody,
                params,
                signal
            });
        },
        [getArticleMetrics]
    );
};

export const useGetArticleMetricsMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useGetArticleMetricsHook>>>,
        TError,
        { data: string[]; params: GetArticleMetricsParams },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useGetArticleMetricsHook>>>,
    TError,
    { data: string[]; params: GetArticleMetricsParams },
    TContext
> => {
    const mutationKey = ['getArticleMetrics'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const getArticleMetrics = useGetArticleMetricsHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useGetArticleMetricsHook>>>,
        { data: string[]; params: GetArticleMetricsParams }
    > = (props) => {
        const { data, params } = props ?? {};

        return getArticleMetrics(data, params);
    };

    return { mutationFn, ...mutationOptions };
};

export type GetArticleMetricsMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetArticleMetricsHook>>>
>;
export type GetArticleMetricsMutationBody = string[];
export type GetArticleMetricsMutationError = ErrorType<ProblemDetails>;

export const useGetArticleMetrics = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useGetArticleMetricsHook>>>,
            TError,
            { data: string[]; params: GetArticleMetricsParams },
            TContext
        >;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useGetArticleMetricsHook>>>,
    TError,
    { data: string[]; params: GetArticleMetricsParams },
    TContext
> => {
    const mutationOptions = useGetArticleMetricsMutationOptions(options);

    return useMutation(mutationOptions, queryClient);
};
/**
 * Get articles by system id
 */
export const useGetArticlesBySystemHook = () => {
    const getArticlesBySystem = useCustomAppClient<GetArticlesBySystemIdResponse>();

    return useCallback(
        (systemId: string, params?: GetArticlesBySystemParams, signal?: AbortSignal) => {
            return getArticlesBySystem({ url: `/articles/system/${systemId}`, method: 'GET', params, signal });
        },
        [getArticlesBySystem]
    );
};

export const getGetArticlesBySystemQueryKey = (systemId?: string, params?: GetArticlesBySystemParams) => {
    return [`/articles/system/${systemId}`, ...(params ? [params] : [])] as const;
};

export const useGetArticlesBySystemQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    systemId: string,
    params?: GetArticlesBySystemParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>, TError, TData>
        >;
    }
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetArticlesBySystemQueryKey(systemId, params);

    const getArticlesBySystem = useGetArticlesBySystemHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>> = ({ signal }) =>
        getArticlesBySystem(systemId, params, signal);

    return { queryKey, queryFn, enabled: !!systemId, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetArticlesBySystemQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>
>;
export type GetArticlesBySystemQueryError = ErrorType<ProblemDetails>;

export function useGetArticlesBySystem<
    TData = Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    systemId: string,
    params: undefined | GetArticlesBySystemParams,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetArticlesBySystem<
    TData = Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    systemId: string,
    params?: GetArticlesBySystemParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetArticlesBySystem<
    TData = Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    systemId: string,
    params?: GetArticlesBySystemParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetArticlesBySystem<
    TData = Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    systemId: string,
    params?: GetArticlesBySystemParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetArticlesBySystemQueryOptions(systemId, params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    query.queryKey = queryOptions.queryKey;

    return query;
}

/**
 * Get articles by setting type
 */
export const useGetArticlesBySettingTypeHook = () => {
    const getArticlesBySettingType = useCustomAppClient<GetArticlesBySettingTypeResponse>();

    return useCallback(
        (params: GetArticlesBySettingTypeParams, signal?: AbortSignal) => {
            return getArticlesBySettingType({ url: `/articles/POI/settingType`, method: 'GET', params, signal });
        },
        [getArticlesBySettingType]
    );
};

export const getGetArticlesBySettingTypeQueryKey = (params?: GetArticlesBySettingTypeParams) => {
    return [`/articles/POI/settingType`, ...(params ? [params] : [])] as const;
};

export const useGetArticlesBySettingTypeQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    params: GetArticlesBySettingTypeParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>, TError, TData>
        >;
    }
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetArticlesBySettingTypeQueryKey(params);

    const getArticlesBySettingType = useGetArticlesBySettingTypeHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>> = ({
        signal
    }) => getArticlesBySettingType(params, signal);

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetArticlesBySettingTypeQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>
>;
export type GetArticlesBySettingTypeQueryError = ErrorType<ProblemDetails>;

export function useGetArticlesBySettingType<
    TData = Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    params: GetArticlesBySettingTypeParams,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetArticlesBySettingType<
    TData = Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    params: GetArticlesBySettingTypeParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetArticlesBySettingType<
    TData = Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    params: GetArticlesBySettingTypeParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetArticlesBySettingType<
    TData = Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    params: GetArticlesBySettingTypeParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetArticlesBySettingTypeQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    query.queryKey = queryOptions.queryKey;

    return query;
}

/**
 * Get all existing articles
 */
export const useGetAllArticlesHook = () => {
    const getAllArticles = useCustomAppClient<GetAllArticlesResponse>();

    return useCallback(
        (params: GetAllArticlesParams, signal?: AbortSignal) => {
            return getAllArticles({ url: `/articles`, method: 'GET', params, signal });
        },
        [getAllArticles]
    );
};

export const getGetAllArticlesQueryKey = (params?: GetAllArticlesParams) => {
    return [`/articles`, ...(params ? [params] : [])] as const;
};

export const useGetAllArticlesQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    params: GetAllArticlesParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>, TError, TData>>;
    }
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetAllArticlesQueryKey(params);

    const getAllArticles = useGetAllArticlesHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>> = ({ signal }) =>
        getAllArticles(params, signal);

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAllArticlesQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>>;
export type GetAllArticlesQueryError = ErrorType<ProblemDetails>;

export function useGetAllArticles<
    TData = Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    params: GetAllArticlesParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllArticles<
    TData = Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    params: GetAllArticlesParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllArticles<
    TData = Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    params: GetAllArticlesParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetAllArticles<
    TData = Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    params: GetAllArticlesParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetAllArticlesQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    query.queryKey = queryOptions.queryKey;

    return query;
}

/**
 * Create or update article
 */
export const useCreateArticleHook = () => {
    const createArticle = useCustomAppClient<GetArticleByIdResponse>();

    return useCallback(
        (createArticleModel: CreateArticleModel, signal?: AbortSignal) => {
            return createArticle({
                url: `/articles`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: createArticleModel,
                signal
            });
        },
        [createArticle]
    );
};

export const useCreateArticleMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useCreateArticleHook>>>,
        TError,
        { data: CreateArticleModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateArticleHook>>>,
    TError,
    { data: CreateArticleModel },
    TContext
> => {
    const mutationKey = ['createArticle'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const createArticle = useCreateArticleHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useCreateArticleHook>>>,
        { data: CreateArticleModel }
    > = (props) => {
        const { data } = props ?? {};

        return createArticle(data);
    };

    return { mutationFn, ...mutationOptions };
};

export type CreateArticleMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useCreateArticleHook>>>>;
export type CreateArticleMutationBody = CreateArticleModel;
export type CreateArticleMutationError = ErrorType<ProblemDetails>;

export const useCreateArticle = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useCreateArticleHook>>>,
            TError,
            { data: CreateArticleModel },
            TContext
        >;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useCreateArticleHook>>>,
    TError,
    { data: CreateArticleModel },
    TContext
> => {
    const mutationOptions = useCreateArticleMutationOptions(options);

    return useMutation(mutationOptions, queryClient);
};
/**
 * Get articles by element type
 */
export const useGetArticlesByElementTypeHook = () => {
    const getArticlesByElementType = useCustomAppClient<GetArticlesByElementTypeResponse>();

    return useCallback(
        (elementType: number, signal?: AbortSignal) => {
            return getArticlesByElementType({
                url: `/articles/articleRepresentations/${elementType}`,
                method: 'GET',
                signal
            });
        },
        [getArticlesByElementType]
    );
};

export const getGetArticlesByElementTypeQueryKey = (elementType?: number) => {
    return [`/articles/articleRepresentations/${elementType}`] as const;
};

export const useGetArticlesByElementTypeQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    elementType: number,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>, TError, TData>
        >;
    }
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetArticlesByElementTypeQueryKey(elementType);

    const getArticlesByElementType = useGetArticlesByElementTypeHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>> = ({
        signal
    }) => getArticlesByElementType(elementType, signal);

    return { queryKey, queryFn, enabled: !!elementType, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetArticlesByElementTypeQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>
>;
export type GetArticlesByElementTypeQueryError = ErrorType<ProblemDetails>;

export function useGetArticlesByElementType<
    TData = Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    elementType: number,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetArticlesByElementType<
    TData = Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    elementType: number,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetArticlesByElementType<
    TData = Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    elementType: number,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetArticlesByElementType<
    TData = Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    elementType: number,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetArticlesByElementTypeQueryOptions(elementType, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    query.queryKey = queryOptions.queryKey;

    return query;
}

/**
 * Get main representation of article by id
 */
export const useGetMainRepresentationByArticleIdHook = () => {
    const getMainRepresentationByArticleId = useCustomAppClient<GetMainRepresentationByArticleIdResponse>();

    return useCallback(
        (id: string, params: GetMainRepresentationByArticleIdParams, signal?: AbortSignal) => {
            return getMainRepresentationByArticleId({
                url: `/articles/articleRepresentations/main/${id}`,
                method: 'GET',
                params,
                signal
            });
        },
        [getMainRepresentationByArticleId]
    );
};

export const getGetMainRepresentationByArticleIdQueryKey = (
    id?: string,
    params?: GetMainRepresentationByArticleIdParams
) => {
    return [`/articles/articleRepresentations/main/${id}`, ...(params ? [params] : [])] as const;
};

export const useGetMainRepresentationByArticleIdQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    id: string,
    params: GetMainRepresentationByArticleIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>,
                TError,
                TData
            >
        >;
    }
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetMainRepresentationByArticleIdQueryKey(id, params);

    const getMainRepresentationByArticleId = useGetMainRepresentationByArticleIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>> = ({
        signal
    }) => getMainRepresentationByArticleId(id, params, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetMainRepresentationByArticleIdQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>
>;
export type GetMainRepresentationByArticleIdQueryError = ErrorType<ProblemDetails>;

export function useGetMainRepresentationByArticleId<
    TData = Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    id: string,
    params: GetMainRepresentationByArticleIdParams,
    options: {
        query: Partial<
            UseQueryOptions<
                Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>,
                TError,
                TData
            >
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetMainRepresentationByArticleId<
    TData = Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    id: string,
    params: GetMainRepresentationByArticleIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>,
                TError,
                TData
            >
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetMainRepresentationByArticleId<
    TData = Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    id: string,
    params: GetMainRepresentationByArticleIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>,
                TError,
                TData
            >
        >;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetMainRepresentationByArticleId<
    TData = Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    id: string,
    params: GetMainRepresentationByArticleIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>,
                TError,
                TData
            >
        >;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetMainRepresentationByArticleIdQueryOptions(id, params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    query.queryKey = queryOptions.queryKey;

    return query;
}

/**
 * Delete the main representation of an article by id
 */
export const useDeletePrefabsByArticleIdHook = () => {
    const deletePrefabsByArticleId = useCustomAppClient<void>();

    return useCallback(
        (id: string) => {
            return deletePrefabsByArticleId({ url: `/articles/articleRepresentations/main/${id}`, method: 'DELETE' });
        },
        [deletePrefabsByArticleId]
    );
};

export const useDeletePrefabsByArticleIdMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeletePrefabsByArticleIdHook>>>,
        TError,
        { id: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeletePrefabsByArticleIdHook>>>,
    TError,
    { id: string },
    TContext
> => {
    const mutationKey = ['deletePrefabsByArticleId'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const deletePrefabsByArticleId = useDeletePrefabsByArticleIdHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeletePrefabsByArticleIdHook>>>,
        { id: string }
    > = (props) => {
        const { id } = props ?? {};

        return deletePrefabsByArticleId(id);
    };

    return { mutationFn, ...mutationOptions };
};

export type DeletePrefabsByArticleIdMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useDeletePrefabsByArticleIdHook>>>
>;

export type DeletePrefabsByArticleIdMutationError = ErrorType<ProblemDetails>;

export const useDeletePrefabsByArticleId = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeletePrefabsByArticleIdHook>>>,
            TError,
            { id: string },
            TContext
        >;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeletePrefabsByArticleIdHook>>>,
    TError,
    { id: string },
    TContext
> => {
    const mutationOptions = useDeletePrefabsByArticleIdMutationOptions(options);

    return useMutation(mutationOptions, queryClient);
};
/**
 * Get lightweight prefab summaries (id, name, contentHash) for an article � use for client-side change detection without loading the full graph
 */
export const useGetPrefabSummariesByArticleIdHook = () => {
    const getPrefabSummariesByArticleId = useCustomAppClient<GetPrefabSummariesByArticleIdResponse>();

    return useCallback(
        (articleId: string, signal?: AbortSignal) => {
            return getPrefabSummariesByArticleId({
                url: `/articles/articleRepresentations/main/${articleId}/summaries`,
                method: 'GET',
                signal
            });
        },
        [getPrefabSummariesByArticleId]
    );
};

export const getGetPrefabSummariesByArticleIdQueryKey = (articleId?: string) => {
    return [`/articles/articleRepresentations/main/${articleId}/summaries`] as const;
};

export const useGetPrefabSummariesByArticleIdQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    articleId: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>, TError, TData>
        >;
    }
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetPrefabSummariesByArticleIdQueryKey(articleId);

    const getPrefabSummariesByArticleId = useGetPrefabSummariesByArticleIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>> = ({
        signal
    }) => getPrefabSummariesByArticleId(articleId, signal);

    return { queryKey, queryFn, enabled: !!articleId, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetPrefabSummariesByArticleIdQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>
>;
export type GetPrefabSummariesByArticleIdQueryError = ErrorType<ProblemDetails>;

export function useGetPrefabSummariesByArticleId<
    TData = Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    articleId: string,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPrefabSummariesByArticleId<
    TData = Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    articleId: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPrefabSummariesByArticleId<
    TData = Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    articleId: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetPrefabSummariesByArticleId<
    TData = Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    articleId: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetPrefabSummariesByArticleIdQueryOptions(articleId, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    query.queryKey = queryOptions.queryKey;

    return query;
}

/**
 * Import a prefab with full data (layers, children, transform, asset reference) as the main representation of an article
 */
export const useImportPrefabByArticleIdHook = () => {
    const importPrefabByArticleId = useCustomAppClient<GetMainRepresentationByArticleIdResponse>();

    return useCallback(
        (createPrefabModel: CreatePrefabModel, params: ImportPrefabByArticleIdParams, signal?: AbortSignal) => {
            return importPrefabByArticleId({
                url: `/articles/articleRepresentations/main`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: createPrefabModel,
                params,
                signal
            });
        },
        [importPrefabByArticleId]
    );
};

export const useImportPrefabByArticleIdMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useImportPrefabByArticleIdHook>>>,
        TError,
        { data: CreatePrefabModel; params: ImportPrefabByArticleIdParams },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useImportPrefabByArticleIdHook>>>,
    TError,
    { data: CreatePrefabModel; params: ImportPrefabByArticleIdParams },
    TContext
> => {
    const mutationKey = ['importPrefabByArticleId'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const importPrefabByArticleId = useImportPrefabByArticleIdHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useImportPrefabByArticleIdHook>>>,
        { data: CreatePrefabModel; params: ImportPrefabByArticleIdParams }
    > = (props) => {
        const { data, params } = props ?? {};

        return importPrefabByArticleId(data, params);
    };

    return { mutationFn, ...mutationOptions };
};

export type ImportPrefabByArticleIdMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useImportPrefabByArticleIdHook>>>
>;
export type ImportPrefabByArticleIdMutationBody = CreatePrefabModel;
export type ImportPrefabByArticleIdMutationError = ErrorType<ProblemDetails>;

export const useImportPrefabByArticleId = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useImportPrefabByArticleIdHook>>>,
            TError,
            { data: CreatePrefabModel; params: ImportPrefabByArticleIdParams },
            TContext
        >;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useImportPrefabByArticleIdHook>>>,
    TError,
    { data: CreatePrefabModel; params: ImportPrefabByArticleIdParams },
    TContext
> => {
    const mutationOptions = useImportPrefabByArticleIdMutationOptions(options);

    return useMutation(mutationOptions, queryClient);
};
/**
 * Insert or update a single prefab by name within an article � use for targeted updates instead of replacing the full set
 */
export const useUpdatePrefabByArticleIdHook = () => {
    const updatePrefabByArticleId = useCustomAppClient<void>();

    return useCallback(
        (articleId: string, updatePrefabModel: UpdatePrefabModel, signal?: AbortSignal) => {
            return updatePrefabByArticleId({
                url: `/articles/articleRepresentations/main/${articleId}/prefab`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: updatePrefabModel,
                signal
            });
        },
        [updatePrefabByArticleId]
    );
};

export const useUpdatePrefabByArticleIdMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdatePrefabByArticleIdHook>>>,
        TError,
        { articleId: string; data: UpdatePrefabModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdatePrefabByArticleIdHook>>>,
    TError,
    { articleId: string; data: UpdatePrefabModel },
    TContext
> => {
    const mutationKey = ['updatePrefabByArticleId'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const updatePrefabByArticleId = useUpdatePrefabByArticleIdHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdatePrefabByArticleIdHook>>>,
        { articleId: string; data: UpdatePrefabModel }
    > = (props) => {
        const { articleId, data } = props ?? {};

        return updatePrefabByArticleId(articleId, data);
    };

    return { mutationFn, ...mutationOptions };
};

export type UpdatePrefabByArticleIdMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useUpdatePrefabByArticleIdHook>>>
>;
export type UpdatePrefabByArticleIdMutationBody = UpdatePrefabModel;
export type UpdatePrefabByArticleIdMutationError = ErrorType<ProblemDetails>;

export const useUpdatePrefabByArticleId = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdatePrefabByArticleIdHook>>>,
            TError,
            { articleId: string; data: UpdatePrefabModel },
            TContext
        >;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdatePrefabByArticleIdHook>>>,
    TError,
    { articleId: string; data: UpdatePrefabModel },
    TContext
> => {
    const mutationOptions = useUpdatePrefabByArticleIdMutationOptions(options);

    return useMutation(mutationOptions, queryClient);
};
/**
 * Replace all prefabs of an article by id
 */
export const useReplacePrefabsByArticleIdHook = () => {
    const replacePrefabsByArticleId = useCustomAppClient<void>();

    return useCallback(
        (id: string, createPrefabModel: CreatePrefabModel[], signal?: AbortSignal) => {
            return replacePrefabsByArticleId({
                url: `/articles/articleRepresentations/main/${id}/replace`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: createPrefabModel,
                signal
            });
        },
        [replacePrefabsByArticleId]
    );
};

export const useReplacePrefabsByArticleIdMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useReplacePrefabsByArticleIdHook>>>,
        TError,
        { id: string; data: CreatePrefabModel[] },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useReplacePrefabsByArticleIdHook>>>,
    TError,
    { id: string; data: CreatePrefabModel[] },
    TContext
> => {
    const mutationKey = ['replacePrefabsByArticleId'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const replacePrefabsByArticleId = useReplacePrefabsByArticleIdHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useReplacePrefabsByArticleIdHook>>>,
        { id: string; data: CreatePrefabModel[] }
    > = (props) => {
        const { id, data } = props ?? {};

        return replacePrefabsByArticleId(id, data);
    };

    return { mutationFn, ...mutationOptions };
};

export type ReplacePrefabsByArticleIdMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useReplacePrefabsByArticleIdHook>>>
>;
export type ReplacePrefabsByArticleIdMutationBody = CreatePrefabModel[];
export type ReplacePrefabsByArticleIdMutationError = ErrorType<ProblemDetails>;

export const useReplacePrefabsByArticleId = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useReplacePrefabsByArticleIdHook>>>,
            TError,
            { id: string; data: CreatePrefabModel[] },
            TContext
        >;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useReplacePrefabsByArticleIdHook>>>,
    TError,
    { id: string; data: CreatePrefabModel[] },
    TContext
> => {
    const mutationOptions = useReplacePrefabsByArticleIdMutationOptions(options);

    return useMutation(mutationOptions, queryClient);
};
